import React, { memo } from 'react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { SelectionToolbarPlugin } from '@atlaskit/editor-plugins/selection-toolbar';

import { CompanyHubBannerLoader } from '@confluence/company-hub/entry-points/CompanyHubBanner';
import {
	EditorControlsBannerLoader,
	LivePagesFeedbackBannerLoader,
	useIsControlsBannerDismissed,
} from '@confluence/editor-action-bar';
import { useOnboardingBannerLogic } from '@confluence/onboarding-editor';
import type { TriggerAnalyticsEventType } from '@confluence/editor-features';
import { fg } from '@confluence/feature-gating';

export type Props = {
	isLivePage: boolean;
	isEmbeddedConfluence: boolean;
	viewMode?: boolean;
	hasEditPermission: boolean;
	shouldRenderPagePreview: boolean;
	spaceKey: string;
	editorFullPagePresetAPI: PublicPluginAPI<[SelectionToolbarPlugin]> | undefined;
	triggerAnalyticsEvent?: TriggerAnalyticsEventType;
	contentId: string;
	version: number;
	isShowingOfflineBanner: boolean;
};

export const FullPageEditorBannerWrapper = memo(
	({
		isLivePage,
		isEmbeddedConfluence,
		viewMode,
		hasEditPermission,
		shouldRenderPagePreview,
		spaceKey,
		triggerAnalyticsEvent,
		contentId,
		version,
		isShowingOfflineBanner,
	}: Props) => {
		const { isControlsBannerDismissed, onDismiss, bannerType } =
			useIsControlsBannerDismissed(isLivePage);

		const { isVisible: isOnboardingEditorDraftBannerVisible } = useOnboardingBannerLogic({
			contentId,
			version,
			isShowingOfflineBanner,
			spaceKey,
			setIsOnboardingBannerDataLoaded: () => {},
		});
		const shouldShowControlsBanner =
			!viewMode &&
			!isControlsBannerDismissed &&
			(!fg('platform_editor_controls_patch_13') || !isOnboardingEditorDraftBannerVisible);

		if (shouldShowControlsBanner) {
			return (
				<EditorControlsBannerLoader
					onDismiss={onDismiss}
					triggerAnalyticsEvent={triggerAnalyticsEvent}
					isLivePage={isLivePage}
					isEmbeddedConfluence={isEmbeddedConfluence}
					bannerType={bannerType}
				/>
			);
		}

		if (isLivePage) {
			return <LivePagesFeedbackBannerLoader isViewOnly={viewMode && !hasEditPermission} />;
		} else {
			if (!shouldRenderPagePreview) {
				return <CompanyHubBannerLoader spaceKey={spaceKey} />;
			}
		}
	},
);
